import React, { Fragment, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { ApiBridgeAlert } from '../../api';
import Table from '../../table/Table';
import { AlertHistory } from '../../alerts/AlertHistory';
import { Button, Dropdown } from 'react-bootstrap';
import { NotesModal, NotesModalFormValues } from '../NotesModal';
import { isNavigableCellClicked } from '../../common/helper';
import FormattedDateTime from '../../common/datetime/FormattedDateTime';
import Has from '../../auth/Has';
import { CAN_ADD_NOTE, CAN_MODIFY_ALERT } from '../../auth/permissions';
import { DropdownWrapper } from '../../common/DropdownWrapper';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { useLocation, useNavigate, Link } from 'react-router';
import { noteInternalApi } from '../../apis';
import { showFeedback } from '../../feedback/feedbackReducer';
import AlertAssignment from '../../common/status/AlertAssignment';
import { InternalApiAlertType } from '../../api-internal';
import AlertRowStatus from '../../alerts/AlertRowStatus';
import { useMe } from '../../organisation/useMe';

interface TableAttribute {
  key: string;
  sortable: boolean;
}

interface AlertProps {
  page: number;
  size: number;
  total: number;
  loading: boolean;
  alerts: Array<ApiBridgeAlert>;
  attributes: TableAttribute[];
  onUpdate: () => void;
  onUpdatePage?: (page: number) => void;
  title?: string;
  expandable?: boolean;
  expandedByDefault?: boolean;
}

export const BridgeAlerts = ({
  alerts,
  page,
  size,
  total,
  loading,
  onUpdate,
  onUpdatePage,
  attributes,
  title = 'Bridge alerts',
  expandable = false,
  expandedByDefault = true,
}: AlertProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { features } = useMe();
  const bridgeAlertPage = isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_INCOMING, features);

  const tableTitle = useMemo(
    () => (
      <>
        {title} {!loading && <span className='sanction-amount-info'>({total})</span>}
      </>
    ),
    [total, loading, title]
  );

  const updatePage = (page: number) => {
    onUpdatePage && onUpdatePage(page);
  };

  const onSubmitNote = async (alertId: number, values: NotesModalFormValues) => {
    if (alertId) {
      const { id } = await noteInternalApi.createNote({
        internalApiCreateNote: {
          alertId,
          content: values.content,
          alertType: InternalApiAlertType.BRIDGE,
        },
      });
      dispatch(showFeedback({ message: `Note #${id} added!`, success: true }));
      onUpdate();
    }
  };

  const navigateTo = (alert: ApiBridgeAlert) => {
    // Do not do navigation if already on the page.
    if (bridgeAlertPage && location.pathname !== `/bridge/alerts/${alert.id}`) {
      return navigate(`/bridge/alerts/${alert.id}`);
    }
  };

  return (
    <Table
      title={tableTitle}
      responsive={false}
      total={total}
      page={page}
      size={size}
      items={alerts}
      fetchPage={updatePage}
      attributes={attributes}
      fetching={loading}
      expandable={expandable}
      expandedByDefault={expandedByDefault}
      emptyMessage='No bridge alerts'
      body={(alerts) =>
        alerts.map((alert: ApiBridgeAlert, index) => (
          <Fragment key={alert.id}>
            <tr
              className={`custom-table-row-${index % 2} ${bridgeAlertPage && 'clickable-row'}`}
              onClick={(event: React.MouseEvent) => {
                isNavigableCellClicked(event) && navigateTo(alert);
              }}
            >
              <td>
                <Link to={`/bridge/alerts/${alert.id}`}>{alert.id}</Link>
              </td>
              <td>
                <FormattedDateTime date={alert.createdTime} showTime />
              </td>
              <td>
                <AlertAssignment
                  alert={alert}
                  alertType={InternalApiAlertType.BRIDGE}
                  afterChange={() => Promise.resolve()}
                  permissions={[CAN_MODIFY_ALERT]}
                />
              </td>
              <td>
                <AlertRowStatus alert={alert} onChange={() => onUpdate()} alertType={InternalApiAlertType.BRIDGE} />
              </td>
              <td>
                <DropdownWrapper align='end'>
                  <Dropdown.Toggle as={Button} size='sm' className='btn-white' id='actions-dropdown'>
                    •••
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <AlertHistory alert={alert} dropdownItem />
                    <Has allPermissions={CAN_ADD_NOTE}>
                      <NotesModal
                        onSubmit={(values: NotesModalFormValues) => onSubmitNote(alert.id, values)}
                        dropdownItem
                      />
                    </Has>
                  </Dropdown.Menu>
                </DropdownWrapper>
              </td>
            </tr>
          </Fragment>
        ))
      }
    />
  );
};

export default BridgeAlerts;
